import { Component } from '@poa/core';
import { observable, Observer } from '@poa/state';
import moment from 'moment';
import React from 'react';
import { AddTransferModal } from '../components/add-transfer-modal';
import DataTable from '../components/data-table/data-table';
import Navbar from '../components/navbar';
import { ProfileModal } from '../components/profile-modal/profile-modal';
import RouteLoading from '../components/route-loading';
import TableFilter, { PageModeTransfers } from '../components/table-filter';
import { ACCESS_LEVEL } from '../store/auth/auth.constants';

class TransfersPage extends React.Component {
  store; // injected by Poa
  actions; // injected by Poa

  localState = observable({
    currentPageMode: PageModeTransfers.operator,
    currentAddMode: PageModeTransfers.operator,
    lastFilters: [],
    isModalOpened: false,
    selectedProfileId: null,
    currentPage: 1
  });

  lastSorting = new Map([['transfer_date', 'desc']]);

  saveColumnPreset = async preset => {
    await this.actions.transfersActions.saveColumnPreset(preset);
  };

  componentDidMount() {
    this.actions.transfersActions.fetch(this.localState.currentPageMode, this.lastSorting);
  }

  onSearchChange = async searchString => {
    this.lastSearch = searchString;
    await this.actions.transfersActions.changeSearch(searchString);
  };


  onSearchEnter = async () => {
    if (this.localState.currentPage !== 1) {
      this.lastPage = 1;
    }

    await this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );

  };

  onDeleteClick = async id => {
    await this.env.http.delete(`/${this.localState.currentPageMode}/transfer/${id}/`);

    this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );
    this.env.alert.clickConfirm();
  };

  onChangeTransferFilters = async filters => {
    if (filters.is_new) {
      this.localState.lastFilters.push({
        type: filters.type,
        value: filters.value,
        label: filters.label,
        isServerSide: filters.isServerSide
      });
    } else {
      this.localState.lastFilters = this.localState.lastFilters.filter(filter => filter.type !== filters.type);
    }

    await this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      1,
      this.lastSorting,
      this.lastSearch
    );
  };

  onPageChange = async page => {
    this.localState.currentPage = page;
    this.lastPage = page;
    await this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );
  };

  onSortChange = (header, mode) => {
    this.lastSorting.set(header, mode);

    this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );
  };

  onAddNewClick = () => {
    this.env.modal({
      html: (
        <AddTransferModal
          manageMode="add"
          initialMode={this.localState.currentPageMode}
          onSave={this.onAddNewTransfer}
          onUpdate={this.onEditTransfer}
          onDelete={this.onDeleteClick}
        />
      )
    });
  };

  onEditClick = async id => {
    const { data } = await this.env.http.get(`/${this.localState.currentPageMode}/transfer/${id}/`);

    await this.env.modal({
      html: (
        <AddTransferModal
          initialMode={this.localState.currentPageMode}
          manageMode="edit"
          onSave={this.onAddNewTransfer}
          onUpdate={this.onEditTransfer}
          onDelete={this.onDeleteClick}
          from={data.from}
          to={data.to}
          transferId={data.id}
          accountId={data.account_id}
          date={moment(data.transfer_date, 'DD.MM.YYYY HH:mm')}
        />
      )
    });
  };

  onModalClose = () => {
    this.localState.isModalOpened = false;
  };

  onAddNewTransfer = ({ from, to, date, type, accountId }) => {
    this.env.http.post(`${type}/transfer/`, {
      account_id: accountId,
      from,
      to,
      transfer_date: date
    });

    this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );

    this.env.alert.clickConfirm();
  };

  onEditTransfer = ({ from, to, date, type, accountId, transferId }) => {
    this.env.http.patch(`${type}/transfer/${transferId}/`, {
      account_id: accountId,
      from,
      to,
      transfer_date: date,
      transfer_id: transferId
    });

    this.actions.transfersActions.refreshTransfers(
      this.localState.currentPageMode,
      this.localState.lastFilters,
      this.lastPage,
      this.lastSorting,
      this.lastSearch
    );

    this.env.alert.clickConfirm();
  };

  onPageModeChange = async event => {
    this.lastSorting = new Map([['transfer_date', 'desc']]);

    switch (event.target.value) {
      case PageModeTransfers.teamlead: {
        await this.actions.transfersActions.fetch(PageModeTransfers.teamlead, this.lastSorting);
        this.localState.currentPageMode = PageModeTransfers.teamlead;
        this.localState.currentAddMode = PageModeTransfers.teamlead;
        break;
      }
      case PageModeTransfers.operator: {
        await this.actions.transfersActions.fetch(PageModeTransfers.operator, this.lastSorting);
        this.localState.currentPageMode = PageModeTransfers.operator;
        this.localState.currentAddMode = PageModeTransfers.operator;
        break;
      }
      case PageModeTransfers.manager: {
        await this.actions.transfersActions.fetch(PageModeTransfers.manager, this.lastSorting);
        this.localState.currentPageMode = PageModeTransfers.manager;
        this.localState.currentAddMode = PageModeTransfers.manager;
        break;
      }
      default:
        console.error('Unknown page type', event.target.value);
        break;
    }
  };

  get canAddNewTransfer() {
    return (
      this.store.auth.accessLevel === ACCESS_LEVEL.agency ||
      this.store.auth.accessLevel === ACCESS_LEVEL.manager
    );
  }

  valueMapper = ({ headerId, data }) => {
    switch (headerId) {
      case 'from':
      case 'to':
        return data.value;
      case 'account_id':
        return data.value;
      default:
        return data.value;
    }
  };

  render() {
    const { filters, selectors, ui } = this.store.transfers;
    const {
      setDates,
      changeGrouping,
      changeStatus,
      changeColumnPreset,
      changeColumnVisibility,
      changeRounding,
      toggleAllColumns,
      changePresetOrder,
      deletePreset
    } = this.actions.transfersActions;

    return (
      <RouteLoading meta={this.store.transfers.meta}>
        <div className="content">
          <Navbar />

          <div className='under-navbar-content'>
            <Observer>
              {() => (
                <TableFilter
                  state={filters}
                  selectors={selectors}
                  onDatesChange={setDates}
                  onGroupingChange={changeGrouping}
                  onStatusChange={changeStatus}
                  onColumnPresetChange={changeColumnPreset}
                  onColumnChange={changeColumnVisibility}
                  onColumnPresetSubmit={this.saveColumnPreset}
                  deletePreset={deletePreset}
                  onIsFractionalChange={changeRounding}
                  onSearchChange={this.onSearchChange}
                  onSearchEnter={this.onSearchEnter}
                  changePresetOrder={changePresetOrder}
                  onAllColumnsToggle={toggleAllColumns}
                  withoutFractionalChange
                  withoutDatesChange
                  withoutStatusChange
                  withoutGroupingChange
                  withoutFilteringChange
                  withSearch={false}
                  withTransferSearch={true}
                  withAdd={this.canAddNewTransfer}
                  onAdd={this.onAddNewClick}
                  getTableNode={() => this.node}
                  currentPageMode={this.localState.currentPageMode}
                  onPageModeTransfersChange={this.onPageModeChange}
                />
              )}
            </Observer>
            <div className="container">
              <div className="inner">
                <div className="inner-scroll stuff-table">
                  <Observer>
                    {() =>
                      selectors.filteredData.map((table, idx) => (
                        <DataTable
                          ref={node => (this.node = node)}
                          key={`${idx}-${this.localState.currentPageMode}`}
                          tableFiltersKey="transfers-filters"
                          datetimesKeys={['transfer_date']}
                          data={table}
                          columnSettings={filters.columnSettings}
                          isDecimalThunk={() => filters.isDecimal}
                          favorites={ui.favorites}
                          toggleFavorite={this.actions.transfersActions.toggleFavorite}
                          requestModalOpen={this.onEditClick}
                          initialSortingMap={this.lastSorting}
                          valueMapper={this.valueMapper}
                          isServerSide
                          name={'transfers'}
                          onRemoveFilter={this.onChangeTransferFilters}
                          onAddFilter={this.onChangeTransferFilters}
                          currentPage={table._meta.page}
                          pageSize={table._meta.page_size}
                          pages={table._meta.pages}
                          onPageChange={this.onPageChange}
                          onSortChange={this.onSortChange}
                          account_filters={null}
                          tooltipContent={({ id, photo, row, hideTooltip }) => (
                            <div className="datatable-row-popup" ref={node => (this.popup = node)}>
                              <img src={photo} alt="" />
                              <div className="content">
                                <button
                                  onClick={() => {
                                    hideTooltip();
                                    this.onEditClick(id);
                                  }}
                                >
                                  <svg
                                    id="icon-pencil"
                                    viewBox="0 0 15 15"
                                    width="100%"
                                    height="100%"
                                  >
                                    <path d="M8.71 2.34l2.84 2.86-7.21 7.24L1.5 9.58zm5 .69L12.3 4.45 9.45 1.59 10.66.37c.5-.49 1.29-.49 1.78 0l1.27 1.28c.39.38.39 1 0 1.38zM.01 13.6c-.05.24.16.45.39.39l3.17-.77-2.84-2.86z" />
                                  </svg>
                                </button>
                              </div>
                              <div className="content">
                                <button
                                  onClick={() => {
                                    hideTooltip();

                                    if (row.account_id && row.account_id.value) {
                                      this.localState.isModalOpened = true;
                                      this.localState.selectedProfileId = row.account_id.value;
                                    }
                                  }}
                                >
                                  <svg viewBox="0 0 14 12" width="100%" height="100%">
                                    <path d="M13.45 2.39a1.8 1.8 0 0 0-1.32-.54H10.5l-.37-.98a1.29 1.29 0 0 0-.51-.61C9.38.09 9.12 0 8.87 0H5.13c-.25 0-.51.09-.75.26-.25.17-.42.37-.51.61l-.37.98H1.87c-.52 0-.96.18-1.32.54-.37.36-.55.79-.55 1.3v6.46c0 .51.18.95.55 1.31.36.36.8.54 1.32.54h10.26c.52 0 .96-.18 1.32-.54.37-.36.55-.8.55-1.31V3.69c0-.51-.18-.94-.55-1.3zM9.31 9.21c-.64.63-1.41.94-2.31.94-.9 0-1.67-.31-2.31-.94a3.12 3.12 0 0 1-.96-2.29c0-.89.32-1.65.96-2.28A3.18 3.18 0 0 1 7 3.69c.9 0 1.67.32 2.31.95.64.63.96 1.39.96 2.28 0 .89-.32 1.65-.96 2.29z" />
                                    <path d="M7 5c-.55 0-1.02.2-1.41.59C5.2 5.98 5 6.45 5 7c0 .55.2 1.02.59 1.41.39.39.86.59 1.41.59.55 0 1.02-.2 1.41-.59C8.8 8.02 9 7.55 9 7c0-.55-.2-1.02-.59-1.41A1.93 1.93 0 0 0 7 5z" />
                                  </svg>
                                </button>
                              </div>
                              <div className="content">
                                <button
                                  onClick={() => {
                                    hideTooltip();
                                    this.onDeleteClick(id);
                                  }}
                                  style={{ right: 80 }}
                                >
                                  <svg
                                    id="icon-pencil"
                                    viewBox="0 0 15 15"
                                    width="100%"
                                    height="100%"
                                  >
                                    <path
                                      xmlns="http://www.w3.org/2000/svg"
                                      d="M3 6v18h18v-18h-18zm5 14c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm5 0c0 .552-.448 1-1 1s-1-.448-1-1v-10c0-.552.448-1 1-1s1 .448 1 1v10zm4-18v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.315c0 .901.73 2 1.631 2h5.712z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                          selectors={selectors}
                        />
                      ))
                    }
                  </Observer>
                </div>
              </div>
            </div>
            <Observer>
              {() => (
                <ProfileModal
                  isOpened={this.localState.isModalOpened}
                  requestClose={this.onModalClose}
                  profileId={this.localState.selectedProfileId}
                />
              )}
            </Observer>
          </div>
        </div>
      </RouteLoading>
    );
  }
}

export default Component()(TransfersPage);
